import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Github, ChevronLeft, ChevronRight } from 'lucide-react';
import AiChatApp from '../assest/AiChatApp.jpg';
import Tayllo from '../assest/Tallyo.jpg';
import capture1 from '../assest/capture1.jpg';
import SIcargo from '../assest/SIcargo.jpg';
import TeslaCloneImg from '../assest/Tela_clonesite.jpg';

const PROJECTS = [
  {
    title: 'AI Chat App',
    description:
      'AI-powered multilingual chatbot built with React and the OpenAI API, working as a coding assistant.',
    image: AiChatApp,
    link: 'https://ai-asistance-chat-app-nl2t.vercel.app/',
    source: 'https://github.com/mtauqeer248/AI-Asistance-Chat-App',
    tags: ['React', 'Tailwind CSS', 'OpenAI API', 'TypeScript'],
    accent: '#34d399',
  },
  {
    title: 'Tallyo — AI Expense Tracker',
    description:
      'Full-stack, GDPR-first expense app for the EU. AI reads receipts, flags doubtful fields for one-tap correction, splits bills to the cent, and includes an AI assistant that can use every feature. Web app plus Android app.',
    image: Tayllo,
    link: 'https://tayllo-web.vercel.app/',
    source: 'https://github.com/mtauqeer248/Tayllo',
    tags: ['Next.js', 'TypeScript', 'Node.js', 'Supabase', 'Groq AI', 'Microservices', 'GDPR'],
    accent: '#2dd4bf',
  },
  {
    title: 'Gourmet Delight',
    description: 'A restaurant food ordering & table reservation site.',
    image: capture1,
    link: 'https://gourment-delight.vercel.app/',
    source: 'https://github.com/mtauqeer248?tab=repositories',
    tags: ['Next.js', 'Tailwind CSS', 'Context API', 'Framer Motion'],
    accent: '#fb923c',
  },
  {
    title: 'S&I Cargo',
    description: 'Cargo booking website.',
    image: SIcargo,
    link: 'https://cargo-service.vercel.app/',
    source: 'https://github.com/mtauqeer248?tab=repositories',
    tags: ['Next.js', 'Tailwind CSS'],
    accent: '#22d3ee',
  },
  {
    title: 'Tesla Clone',
    description: 'A front-end Tesla clone website.',
    image: TeslaCloneImg,
    link: 'https://tesla-clone-chi-three.vercel.app/',
    source: 'https://github.com/mtauqeer248?tab=repositories',
    tags: ['Next.js', 'Tailwind CSS', 'Redux Toolkit'],
    accent: '#94a3b8',
  },
];

const AUTOPLAY_MS = 6000;

const Portfolio = () => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCurrent((c) => (c + 1) % PROJECTS.length), []);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + PROJECTS.length) % PROJECTS.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const project = PROJECTS[current];

  return (
    <section
      id="portfolio"
      className="relative py-20 sm:py-28 bg-ink-950 overflow-hidden font-body"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 bg-grain pointer-events-none" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] rounded-full blur-3xl opacity-20 transition-colors duration-700 pointer-events-none"
        style={{ backgroundColor: project.accent }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="section-eyebrow">Selected work</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold font-display">
            <span className="text-gradient">Things I've built</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="relative rounded-2xl overflow-hidden glass aspect-video">
            <AnimatePresence mode="wait">
              <motion.img
                key={project.title}
                src={project.image}
                alt={project.title}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 via-transparent to-transparent" />

            <button
              onClick={prev}
              aria-label="Previous project"
              className="interactive absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next project"
              className="interactive absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={project.title}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.4 }}
              className="glass rounded-2xl p-6 sm:p-8"
            >
              <span className="text-xs font-mono text-white/40">
                {String(current + 1).padStart(2, '0')} / {String(PROJECTS.length).padStart(2, '0')}
              </span>
              <h3 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-white">
                {project.title}
              </h3>
              <p className="mt-3 text-white/60 leading-relaxed">{project.description}</p>

              <div className="mt-5 flex flex-wrap gap-2">
                {project.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-mono text-white/60 border border-white/10"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="interactive inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-ink-950 transition-transform hover:scale-105"
                  style={{ backgroundColor: project.accent }}
                >
                  <ExternalLink className="w-4 h-4" />
                  Live demo
                </a>
                <a
                  href={project.source}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="interactive inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold glass text-white hover:border-white/25 transition-colors"
                >
                  <Github className="w-4 h-4" />
                  Source
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Thumbnail / dot navigation */}
        <div className="mt-8 flex items-center justify-center gap-3">
          {PROJECTS.map((p, i) => (
            <button
              key={p.title}
              onClick={() => setCurrent(i)}
              aria-label={`Go to ${p.title}`}
              className="interactive relative h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === current ? '2.5rem' : '1rem',
                backgroundColor: i === current ? p.accent : 'rgba(255,255,255,0.15)',
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
